import React, { useCallback, useState, Component } from 'react';
/**
 * PracticeSession — Core practice/assessment session orchestrator.
 *
 * DDD co-locatie: moved from components/practice/ in s69.
 *
 * F-FE-1 god-component split (K12 decision — custom hooks, not
 * mini-components). This file is now a thin orchestrator that composes four
 * concern-owning hooks and renders. See:
 *   • useFeedbackOrchestration — celebration / streak / XP presentation state
 *   • useQuestionFlow          — question lifecycle + answer pipeline + skip
 *   • useSessionTimer          — Fluency / skip-eligibility / idle timers
 *   • useExitGuards            — exit-confirm + Escape handling
 *
 * All four hooks are invoked UNCONDITIONALLY above every early return so the
 * hook count is stable across the loading / error / empty / summary / normal
 * renders (avoids React #310). Earlier extracted UI: 7 full-page states +
 * exit modal → practiceSessionStates.tsx; Fluency feedback → FluencyFeedbackStates.tsx.
 */
import { motion, AnimatePresence } from 'framer-motion';
import { type Question, type ModuleId, getLearningGoal, MODULES, getModuleHexColor, getModuleTint, getFamilyDefinition } from '../../content';
import { getFamilyMasteryScore } from '../../mastery';
import { Button } from '../../components/ui/Button';
import { QuestionRendererFactory } from './renderers/QuestionRendererFactory';
import { FeedbackDisplay } from './feedback/FeedbackDisplay';
import { PracticeHeaderMinimal } from './PracticeHeaderMinimal';
import { QuestionCardClean } from './QuestionCardClean';
import { ComboIndicator } from '../../components/shared/ComboIndicator';
import { CheatSheetDrawer } from '../../support';
import { ScratchpadDrawer } from './ScratchpadDrawer';
import { SessionSummary } from './SessionSummary';
import { SessionLoadingState, SessionErrorState, SessionEmptyNoContent, SessionEmptyAllMastered, SessionEmptyFallback, SessionSafetyError, PracticeExitModal } from './practiceSessionStates';
import { FluencyCorrectState, FluencyIncorrectState } from './FluencyFeedbackStates';
import { getMotivationalMessage, getStreakMessage, getLevelTitle } from '../../utils/motivationalMessages';
import { useAppStore } from '../../app/hooks/useAppStore';
import { useMotionBudget } from '../../app/hooks/useMotionBudget';
import { useFocusTrap } from '../../app/hooks/useFocusTrap';
import { ArrowRight, NotebookPen, HelpCircle } from 'lucide-react';
import { validateSessionAvailability } from '../logic/sessionValidation';
import { error as logError } from '../../utils/logger';
import { DjayIdentity } from '../../mascot';
import { useFeedbackOrchestration } from '../hooks/useFeedbackOrchestration';
import { useQuestionFlow } from '../hooks/useQuestionFlow';
import { useSessionTimer } from '../hooks/useSessionTimer';
import { useExitGuards } from '../hooks/useExitGuards';
// ── Types ────────────────────────────────────────────────────
interface PracticeSessionProps {
  moduleId: ModuleId | null;
  questions: Question[];
  onComplete: (summary?: {
    answeredCount: number;
  }) => void;
  onExit: () => void;
  onAnswer: (questionId: string, isCorrect: boolean, userChoice: number, payload?: string, errorType?: string, delta?: number, timeMs?: number, helpUsed?: boolean, skipped?: boolean) => void;
  cheatSheetViewed: boolean;
  onCheatSheetView: () => void;
  isCrossModule?: boolean;
  isFluency?: boolean;
  labMode?: string;
  pendingLevelUp?: number | null;
  xp?: number;
  onClearPendingLevelUp?: () => void;
}
// ── Component ────────────────────────────────────────────────
export function PracticeSession({
  moduleId,
  questions,
  onComplete,
  onExit,
  onAnswer,
  cheatSheetViewed,
  onCheatSheetView,
  isCrossModule = false,
  isFluency = false,
  labMode,
  pendingLevelUp = null,
  xp = 0,
  onClearPendingLevelUp
}: PracticeSessionProps) {
  const {
    state,
    selectedGroup,
    groupVersion,
    isInitialized,
    manualPeriod,
    updateDailyChallengeProgress
  } = useAppStore();
  const {
    shouldAnimate
  } = useMotionBudget();
  // Mode flags
  const isChallenge = labMode === 'challenge';
  const showFeedback = true;
  const showStreak = true;
  const showCheatSheet = !isCrossModule;
  // ── UI-only state (drawers) ──────────────────────────────
  const [isCheatSheetOpen, setIsCheatSheetOpen] = useState(false);
  const [isScratchpadOpen, setIsScratchpadOpen] = useState(false);
  const handleQuestionChange = useCallback(() => setIsScratchpadOpen(false), []);
  // ── Concern hooks (ALL unconditional, above every early return) ──
  const feedback = useFeedbackOrchestration();
  const flow = useQuestionFlow({
    moduleId,
    questions,
    isCrossModule,
    isFluency,
    isChallenge,
    showCheatSheet,
    labMode,
    pendingLevelUp,
    onClearPendingLevelUp,
    state,
    selectedGroup,
    groupVersion,
    isInitialized,
    manualPeriod,
    updateDailyChallengeProgress,
    onAnswer,
    feedback,
    isCheatSheetOpen,
    onQuestionChange: handleQuestionChange,
    motivational: getMotivationalMessage,
    streakMessage: getStreakMessage
  });
  const timer = useSessionTimer({
    isFluency,
    phase: flow.phase,
    currentQuestionIndex: flow.currentQuestionIndex,
    isCheatSheetOpen,
    sessionQuestions: flow.sessionQuestions,
    questionStartTimeRef: flow.questionStartTimeRef
  });
  const dismissSkipConfirm = useCallback(() => {
    flow.setShowSkipConfirm(false);
  }, [flow.setShowSkipConfirm]);
  const skipConfirmRef = useFocusTrap(flow.showSkipConfirm);
  const exit = useExitGuards({
    phase: flow.phase,
    answeredCount: flow.sessionAnswers.length,
    isCheatSheetOpen,
    isSkipConfirmOpen: flow.showSkipConfirm,
    onDismissSkipConfirm: dismissSkipConfirm,
    onExit
  });
  // Final skip visibility = base eligibility (from flow) AND the 20 s gate
  // (from the timer). See useQuestionFlow.canShowSkipBase.
  const canShowSkip = flow.canShowSkipBase && timer.canSkipTimePassed;
  const {
    sessionQuestions,
    currentQuestionIndex,
    currentQuestion,
    isLastQuestion,
    phase,
    sessionInitialized,
    initError,
    preFamilyMastery,
    sessionAnswers,
    userAnswerIndex,
    userTypedAnswer,
    actualIsCorrect,
    interactiveReady,
    shouldValidateInteractive,
    setInteractiveReady,
    challengeTierDowngraded,
    helpUsedForCurrentQuestionRef,
    handleRendererAnswer,
    submitAnswer,
    nextQuestion,
    resetSession,
    showSkipConfirm,
    setShowSkipConfirm,
    setCheatSheetEverOpened,
    skipFlashVisible,
    requestSkip,
    handleSkipConfirm,
    handleRendererFailure,
    isInteractiveQuestion
  } = flow;
  const handleCheatSheetClose = () => {
    setIsCheatSheetOpen(false);
    if (!cheatSheetViewed) onCheatSheetView();
  };
  // ── Early returns (full-page states) ─────────────────────
  if (!isInitialized || !sessionInitialized) return <SessionLoadingState data-id="element-11112" data-mp="2b20017aol7yo" />;
  if (initError === 'generation_failed') return <SessionErrorState onExit={onExit} data-id="element-11113" data-mp="2b3tcj37z6mel" />;
  if (sessionInitialized && sessionQuestions.length === 0) {
    const validationReport = moduleId ? validateSessionAvailability(moduleId, selectedGroup) : null;
    const hasContent = validationReport?.isValid ?? false;
    const hasAnyProgress = state.familyMastery && Object.keys(state.familyMastery).some(fId => state.familyMastery![fId].attempts > 0);
    if (!hasContent) return <SessionEmptyNoContent onExit={onExit} data-id="element-11114" data-mp="31bzemvw8d5jr" />;
    if (hasAnyProgress) return <SessionEmptyAllMastered onExit={onExit} data-id="element-11115" data-mp="36atw9uigoo2a" />;
    return <SessionEmptyFallback onExit={onExit} data-id="element-11116" data-mp="36cn61iodfoei" />;
  }
  if (!currentQuestion && sessionQuestions.length > 0) {
    logError('Current question is undefined but session has questions. Index:', currentQuestionIndex);
    return <SessionSafetyError onExit={onExit} data-id="element-11117" data-mp="3k9tkj122b0xx" />;
  }
  // ── Summary phase ────────────────────────────────────────
  if (phase === 'summary') {
    return <SessionSummary moduleId={moduleId ?? '' as ModuleId} sessionQuestions={sessionQuestions} sessionAnswers={sessionAnswers} preFamilyMastery={preFamilyMastery} isFluency={isFluency} labMode={labMode} onExit={() => onComplete({
      answeredCount: sessionQuestions.length
    })} onPracticeAgain={resetSession} xpGained={feedback.totalXpGained} data-id="element-11118" data-mp="3p2cxpwwu7u27" />;
  }
  if (!currentQuestion) return <SessionLoadingState data-id="element-11119" data-mp="27wvbauwh8st4" />;
  // §3.1 v2 APP_FEEL_2027 — Per-wereld theming: alleen de header draagt een
  // zachte gradient; de body blijft neutraal (slate-50). Fluency mode (amber)
  // blijft z'n eigen visuele wereld.
  const moduleColorClass = moduleId ? MODULES.find(m => m.id === moduleId)?.color || 'text-indigo-500' : 'text-indigo-500';
  const sessionModuleColor = getModuleHexColor(moduleColorClass);
  const worldTint = isFluency ? undefined : getModuleTint(moduleColorClass);
  // Kladpapiertje is alleen zinvol bij rekenkundige modules (hasNumericContent).
  const scratchpadEligible = Boolean(moduleId && !isFluency && MODULES.find(m => m.id === moduleId)?.hasNumericContent);
  const familyMasteryPercent = currentQuestion?.familyId ? getFamilyMasteryScore(state.familyMastery ?? {}, currentQuestion.familyId, selectedGroup) : undefined;
  const familyLabel = currentQuestion?.familyId ? getFamilyDefinition(currentQuestion.familyId)?.label : undefined;
  // ── Main render ──────────────────────────────────────────
  return <div className="min-h-screen flex flex-col font-sans bg-slate-50" onMouseMove={timer.resetIdleTimer} onTouchStart={timer.resetIdleTimer} onClick={timer.resetIdleTimer} onKeyDown={timer.resetIdleTimer} data-id="element-11120" data-mp="w6766p904bxg">
      {showCheatSheet && <CheatSheetDrawer isOpen={isCheatSheetOpen} onClose={handleCheatSheetClose} moduleId={moduleId!} subSkill={currentQuestion.subSkill} familyId={currentQuestion.familyId} data-id="element-11121" data-mp="32wescbtag0bi" />}

      {/* Kladpapiertje — alleen voor numerieke vakken. Puur tooling. */}
      {scratchpadEligible && <ScratchpadDrawer isOpen={isScratchpadOpen} onClose={() => setIsScratchpadOpen(false)} resetKey={currentQuestion?.id} questionText={currentQuestion?.question} data-id="element-11122" data-mp="23hxu1tqjm013" />}

      <PracticeHeaderMinimal currentQuestion={currentQuestionIndex + 1} totalQuestions={sessionQuestions.length} streak={showStreak ? state.streak?.currentTotalStreak || 0 : undefined} speedStreak={feedback.speedStreak} isFluency={isFluency} elapsedMs={isFluency && phase === 'question' ? timer.elapsedMs : undefined} moduleColor={sessionModuleColor} worldTint={worldTint} onExit={exit.requestExit} onToggleCheatSheet={showCheatSheet ? () => {
      setIsCheatSheetOpen(true);
      feedback.setCheatSheetNudge(false);
      timer.setIsIdle(false);
      // Mark help-used the moment the cheatsheet opens during a
      // live question phase. Stays flagged for this question.
      if (phase === 'question') {
        helpUsedForCurrentQuestionRef.current = true;
      }
      // Session-scoped: unlocks the "Ik weet het echt niet" gate.
      setCheatSheetEverOpened(true);
    } : undefined} isCheatSheetOpen={isCheatSheetOpen} nudgeActive={feedback.cheatSheetNudge} isIdle={timer.isIdle} onIdleDismiss={timer.dismissIdle} learningGoal={currentQuestion ? getLearningGoal(currentQuestion.familyId) : undefined} data-id="element-11123" data-mp="2dfke0ump8hrx" />

      <div className={`flex-1 max-w-4xl mx-auto w-full px-3 sm:px-4 md:px-6 py-3 sm:py-4 md:py-6 flex flex-col min-h-[calc(100vh-64px)] justify-center ${isFluency ? 'bg-amber-50/20' : ''}`} data-id="element-11124" data-mp="bv16k2hpdhay">
        {/* Challenge tier-downgrade encouragement banner */}
        <AnimatePresence data-id="element-11125" data-mp="2b16fb2lwsvli">
          {challengeTierDowngraded && isChallenge && <motion.div key="challenge-downgrade" initial={shouldAnimate ? {
          opacity: 0,
          y: -20,
          scale: 0.95
        } : {
          opacity: 1
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={shouldAnimate ? {
          opacity: 0,
          y: -20,
          scale: 0.95
        } : {
          opacity: 0
        }} transition={shouldAnimate ? {
          type: 'spring',
          stiffness: 350,
          damping: 30
        } : {
          duration: 0
        }} className="mb-4 flex items-center gap-3" data-id="element-11126" data-mp="1wca817rofmly">
              <motion.div animate={shouldAnimate ? {
            y: [0, -3, 0]
          } : {
            y: 0
          }} transition={shouldAnimate ? {
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          } : {
            duration: 0
          }} className="flex-shrink-0" data-id="element-11127" data-mp="1618bi0qs8wx3">
                <DjayIdentity size="md" emotion="pointing" animate={shouldAnimate} data-id="element-11128" data-mp="3s19trybl3r7i" />
              </motion.div>
              <div className="relative flex-1 max-w-[320px]" data-id="element-11129" data-mp="5vwdmmiyxyld">
                <div className="bg-violet-50 rounded-2xl shadow-lg border border-violet-200 px-4 py-3" data-id="element-11130" data-mp="atxtkyl3lhsm">
                  <p className="text-violet-900 font-bold text-sm leading-relaxed" data-id="element-11131" data-mp="39btoyysv9cla">
                    Lastige vragen! 💪
                  </p>
                  <p className="text-violet-700 text-sm mt-1" data-id="element-11132" data-mp="3ebg3rcocf0w7">
                    Even terug naar de basis — dan bouwen we weer op.
                  </p>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Combo Indicator */}
        {feedback.currentStreak >= 2 && <div className="flex justify-center mb-4" data-id="element-11133" data-mp="2qj9boppnhg2l">
            <ComboIndicator combo={feedback.currentStreak} data-id="element-11134" data-mp="2vj555v0q1p3w" />
          </div>}

        <div className="flex-1 flex flex-col relative" data-id="element-11135" data-mp="1cpc0krlaqbfs">
          <AnimatePresence mode="wait" data-id="element-11136" data-mp="1cmpukwe203zo">
            {phase === 'question' && <motion.div key={currentQuestion.id} initial={shouldAnimate ? {
            opacity: 0,
            y: 30,
            scale: 0.97
          } : {
            opacity: 1
          }} animate={{
            opacity: 1,
            x: 0,
            y: 0,
            scale: 1
          }} exit={shouldAnimate ? {
            opacity: 0,
            y: 80,
            rotateZ: -3,
            scale: 0.95
          } : {
            opacity: 0
          }} transition={shouldAnimate ? {
            type: 'spring',
            stiffness: 300,
            damping: 30,
            exit: {
              duration: 0.25,
              ease: 'easeIn'
            }
          } : {
            duration: 0
          }} className="w-full" data-id="element-11137" data-mp="18o8kq5390fek">
                <div data-id="element-11138" data-mp="k1sri3s9fr95">
                  <QuestionCardClean question={currentQuestion} isFluency={isFluency} moduleColor={sessionModuleColor} data-id="element-11139" data-mp="k4f2yei9uqwn">
                    <QuestionRendererFactory question={currentQuestion} onAnswer={handleRendererAnswer} onReadyChange={setInteractiveReady} shouldValidate={shouldValidateInteractive} moduleColor={sessionModuleColor} onSkipQuestion={handleRendererFailure} data-id="element-11140" data-mp="y2dc95hqjx5a" />
                  </QuestionCardClean>
                </div>
                <motion.div initial={shouldAnimate ? {
              opacity: 0,
              y: 20
            } : {
              opacity: 1
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={shouldAnimate ? {
              delay: 0.2
            } : {
              duration: 0
            }} className="mt-4 sm:mt-6 md:mt-8 pb-4 flex justify-center" data-id="element-11141" data-mp="2x8qynzpw2ks3">
                  <motion.div className="w-full sm:w-auto rounded-2xl" animate={shouldAnimate && !(isInteractiveQuestion(currentQuestion) ? !interactiveReady : userAnswerIndex === null) ? {
                boxShadow: ['0 0 0 0px rgba(0, 0, 0, 0)', `0 0 0 8px ${sessionModuleColor}26`, '0 0 0 0px rgba(0, 0, 0, 0)']
              } : {}} transition={{
                duration: 2,
                repeat: Infinity
              }} data-id="element-11142" data-mp="1idsitxpeslvl">
                    <Button size="xl" disabled={isInteractiveQuestion(currentQuestion) ? !interactiveReady : userAnswerIndex === null} onClick={submitAnswer} className="w-full sm:w-auto sm:min-w-[280px] md:min-w-[360px] shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1 rounded-2xl text-fluid-lg" data-id="element-11143" data-mp="llxaootf2lcb">
                      <span className="mr-2 font-black" data-id="element-11144" data-mp="x1tmnxtjve8d">Controleren</span>
                      <ArrowRight className="w-5 h-5" strokeWidth={3} data-id="element-11145" data-mp="wyxn65220hht" />
                    </Button>
                  </motion.div>
                </motion.div>
              </motion.div>}

            {phase === 'fluency-correct' && <FluencyCorrectState currentQuestionIndex={currentQuestionIndex} speedStreak={feedback.speedStreak} xpGained={feedback.xpGained} data-id="element-11146" data-mp="i2zl665g8ixo" />}

            {phase === 'fluency-incorrect' && <FluencyIncorrectState currentQuestionIndex={currentQuestionIndex} currentQuestion={currentQuestion} userTypedAnswer={userTypedAnswer} userAnswerIndex={userAnswerIndex} onShowFullFeedback={() => flow.setPhase('feedback')} onNext={nextQuestion} data-id="element-11147" data-mp="22mhos0pysl9e" />}

            {phase === 'feedback' && showFeedback && <motion.div key={`feedback-${currentQuestionIndex}`} initial={shouldAnimate ? {
            opacity: 0,
            y: 24,
            scale: 0.97
          } : {
            opacity: 1
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} transition={shouldAnimate ? {
            type: 'spring',
            stiffness: 350,
            damping: 30
          } : {
            duration: 0
          }} className="w-full" data-id="element-11148" data-mp="2lkan0ppn870g">
                <FeedbackDisplay question={currentQuestion} wasCorrect={isInteractiveQuestion(currentQuestion) ? actualIsCorrect === true : userAnswerIndex !== null && userAnswerIndex === currentQuestion.correctAnswer} userAnswer={isInteractiveQuestion(currentQuestion) ? userTypedAnswer : userAnswerIndex !== null ? currentQuestion.options?.[userAnswerIndex] ?? '' : ''} userAnswerIndex={userAnswerIndex ?? undefined} motivationalText={feedback.motivationalText} xpGained={feedback.xpGained} onNext={nextQuestion} isLastQuestion={isLastQuestion} mode={isFluency ? 'compact' : 'full'} streak={showStreak ? state.streak?.currentTotalStreak || 0 : undefined} levelUp={pendingLevelUp ? {
              level: pendingLevelUp,
              xp,
              title: getLevelTitle(pendingLevelUp)
            } : null} streakMilestone={feedback.streakMilestone} dailyGoalReached={feedback.dailyGoalReached} onClearLevelUp={onClearPendingLevelUp} moduleColor={sessionModuleColor} familyMasteryPercent={familyMasteryPercent} familyLabel={familyLabel} data-id="element-11149" data-mp="3ln0ew8q7sxw2" />
              </motion.div>}
          </AnimatePresence>
          {/* Skip link */}
          {canShowSkip && <AnimatePresence data-id="element-11150" data-mp="sohx80mgvz36">
              <motion.div key="skip-link" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.2
          }} className="mt-4 sm:mt-6 md:mt-8 pb-4 flex justify-center" data-id="element-11151" data-mp="snqdpo659rt9">
                <motion.button onClick={() => requestSkip(canShowSkip)} whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} className="flex items-center gap-2 px-4 py-2 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-full font-medium" data-id="element-11152" data-mp="3s3vb187vq7gf">
                  <HelpCircle className="w-4 h-4" data-id="element-11153" data-mp="3fc77trb6u3dl" />
                  <span className="text-sm" data-id="element-11154" data-mp="3fanua8gx26fi">Ik weet het echt niet</span>
                </motion.button>
              </motion.div>
            </AnimatePresence>}
        </div>

        <PracticeExitModal show={exit.showExitConfirm} answeredCount={sessionAnswers.length} totalQuestions={sessionQuestions.length} onContinue={() => exit.setShowExitConfirm(false)} onStop={() => {
        exit.setShowExitConfirm(false);
        onExit();
      }} data-id="element-11155" data-mp="36ck6t6mdezl2" />

        {/* Skip confirmation — Djay-styled sheet. Skipping is a healthy
            learning move, so the framing is warm and reassuring. */}
        <AnimatePresence data-id="element-11156" data-mp="pmwuf4z88b8p">
          {showSkipConfirm && <motion.div key="skip-confirm-backdrop" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4" onClick={dismissSkipConfirm} data-id="element-11157" data-mp="y45lzc78zjv0">
              <motion.div ref={skipConfirmRef} role="dialog" aria-modal="true" aria-labelledby="skip-confirm-title" initial={shouldAnimate ? {
            opacity: 0,
            scale: 0.92,
            y: 16
          } : {
            opacity: 1
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={shouldAnimate ? {
            opacity: 0,
            scale: 0.95,
            y: 8
          } : {
            opacity: 0
          }} transition={shouldAnimate ? {
            type: 'spring',
            stiffness: 320,
            damping: 26
          } : {
            duration: 0
          }} className="relative bg-white rounded-3xl shadow-2xl max-w-sm w-full overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-11158" data-mp="j6d96rt359ki">
                {/* Djay header strip — soft amber. */}
                <div className="px-6 pt-6 pb-4 bg-gradient-to-b from-amber-50 to-white flex items-start gap-4" data-id="element-11159" data-mp="2k3n87aiyjson">
                  <motion.div animate={shouldAnimate ? {
                y: [0, -3, 0]
              } : {
                y: 0
              }} transition={shouldAnimate ? {
                duration: 2.4,
                repeat: Infinity,
                ease: 'easeInOut'
              } : {
                duration: 0
              }} className="flex-shrink-0" data-id="element-11160" data-mp="2ouls0k35n8wn">
                    <DjayIdentity size="md" emotion="encouraging" animate={shouldAnimate} data-id="element-11161" data-mp="1icqagjjdqzv2" />
                  </motion.div>
                  <div className="flex-1 min-w-0 pt-1" data-id="element-11162" data-mp="2e13h4r3dy5k1">
                    <h2 id="skip-confirm-title" className="text-lg font-black text-slate-900 leading-tight" data-id="element-11163" data-mp="2931tdsv0li28">
                      Helemaal oké!
                    </h2>
                    <p className="text-sm text-slate-700 mt-1.5 leading-snug" data-id="element-11164" data-mp="205htoocsvttq">
                      Zal ik een andere vraag voor je pakken? Daar leer je net
                      zoveel van.
                    </p>
                  </div>
                </div>

                {/* Buttons — positive primary framing. */}
                <div className="px-6 pb-6 pt-2 flex flex-col gap-2" data-id="element-11165" data-mp="2j2akmdyhwikw">
                  <button type="button" onClick={handleSkipConfirm} className="w-full rounded-2xl bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 text-sm shadow-md hover:shadow-lg transition-all" data-id="element-11166" data-mp="2j20vstp6shgs">
                    Ja, andere vraag graag
                  </button>
                  <button type="button" onClick={dismissSkipConfirm} className="w-full rounded-2xl bg-white hover:bg-slate-50 text-slate-700 font-bold py-3 text-sm border border-slate-200 transition-colors" data-id="element-11167" data-mp="3o0e8yh14dhcm">
                    Nog even proberen
                  </button>
                </div>
              </motion.div>
            </motion.div>}
        </AnimatePresence>

        {/* Skip flash — brief Djay reassurance after a confirmed skip. */}
        <AnimatePresence data-id="element-11168" data-mp="flw4epae0jxr">
          {skipFlashVisible && <motion.div key="skip-flash" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4" data-id="element-11169" data-mp="kkqmtq4g06hc">
              <motion.div initial={{
            scale: 0.9,
            y: 8
          }} animate={{
            scale: 1,
            y: 0
          }} exit={{
            scale: 0.95
          }} transition={{
            type: 'spring',
            stiffness: 320,
            damping: 24
          }} className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-5 flex items-center gap-4" role="status" aria-live="polite" data-id="element-11170" data-mp="1mc3h4w1l4gkz">
                <div className="flex-shrink-0" data-id="element-11171" data-mp="2xzd7siovvj89">
                  <DjayIdentity size="md" emotion="encouraging" animate={shouldAnimate} data-id="element-11172" data-mp="2xxk2b6q0b51u" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-11173" data-mp="r9jq9jt6aok4">
                  <p className="text-base font-bold text-slate-900 leading-snug" data-id="element-11174" data-mp="mctbr0x0o10t">
                    Geen zorgen — die slaan we even over.
                  </p>
                  <p className="text-sm text-slate-600 mt-1 leading-snug" data-id="element-11175" data-mp="105sr92wstv8d">
                    We komen er later op terug. 💪
                  </p>
                </div>
              </motion.div>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Floating scratchpad trigger — numerieke modules, alleen tijdens de
                      vraag-fase. */}
      {scratchpadEligible && phase === 'question' && <motion.button type="button" onClick={() => {
      setIsScratchpadOpen(true);
      // Opening the scratchpad is active engagement, not idleness.
      timer.resetIdleTimer();
    }} initial={{
      opacity: 0,
      scale: 0.6,
      y: 12
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} transition={{
      type: 'spring',
      stiffness: 320,
      damping: 22,
      delay: 0.3
    }} whileHover={shouldAnimate ? {
      scale: 1.06,
      y: -2
    } : undefined} whileTap={shouldAnimate ? {
      scale: 0.94
    } : undefined} className="
            fixed z-30
            right-4 sm:right-6
            bottom-[max(1rem,env(safe-area-inset-bottom))]
            sm:bottom-6
            flex items-center gap-2
            px-3.5 py-3 sm:px-4 sm:py-3
            rounded-full
            bg-amber-50 border border-amber-300
            text-amber-800 font-bold
            shadow-lg shadow-amber-900/10
            hover:bg-amber-100 hover:border-amber-400
            transition-colors
          " aria-label="Kladpapiertje openen" title="Kladpapiertje — schrijf of typ je tussenstappen" data-id="element-11176" data-mp="cdlu41j91k7w">
          <NotebookPen className="w-5 h-5" strokeWidth={2.4} data-id="element-11177" data-mp="32neapaqbek3z" />
          <span className="hidden sm:inline text-sm" data-id="element-11178" data-mp="2ymkqosfztpgd">Klad</span>
        </motion.button>}
    </div>;
}